import { Module, VuexModule, Action, Mutation, getModule } from "vuex-module-decorators";
import { store } from "commonui";
import { IVersionGroupService, VersionGroupModel, VersionGroupService } from "@/services";
import { VersionGroupModule } from "@/modules/versiongroup";
import { EmptyGuid, MethodologyTypeItemsWithAll } from "@/util/constants";
import { LessonPlanActionStatus, CurriculumType, MethodologyTypeEnum } from "@/util/enum";
import { getSelectedVersionGroups, second2Minute } from "@/util/func";
import {
	ILessonPlanListModel,
	ILessonPlanTemplateModel,
	ILessonPlanVersionModel,
	sortLessonPlanVersions
} from "@/models/lesson-plan";
import {
	ILessonPlanListService,
	ILessonPlanVersionService,
	LessonPlanListService,
	LessonPlanVersionService
} from "@/services/lesson-plan";
import { IUnitListManager, UnitListManagerService } from "@/services/text-guardian/unit-list";
import { VCSLevelModel } from "@/models/title-list-model";
import { TitleListService } from "@/services/title-list-service";
import MasterPlanState from "@/modules/master-plan";
import { BlobHelper } from "@/util/helper";

function getLicenseTypeByLessonPlanVersion(
	lessonPlanVersion: ILessonPlanVersionModel,
	selectedGSVersion: string,
	selectedLSVersion: string
): CurriculumType | undefined {
	if (selectedGSVersion === "-1" && selectedLSVersion === "-1") {
		return undefined;
	}
	return lessonPlanVersion.versionGroupId === selectedGSVersion
		? CurriculumType.GrapeSEED
		: lessonPlanVersion.versionGroupId === selectedLSVersion
		? CurriculumType.LittleSEED
		: undefined;
}

function filterUnits(units: VCSLevelModel[], curriculumType?: CurriculumType): VCSLevelModel[] {
	return curriculumType === undefined
		? []
		: units.filter((au) => au.curriculumType === curriculumType).sort((ua, ub) => ua.unit - ub.unit);
}

function lessonPlanSorter(lessonPlanA: ILessonPlanListModel, lessonPlanB: ILessonPlanListModel): number {
	return (lessonPlanA.sequence as number) - (lessonPlanB.sequence as number);
}

function formatLessonPlans(
	lessonPlans: ILessonPlanListModel[],
	methodology?: MethodologyTypeEnum
): ILessonPlanListModel[] {
	return lessonPlans.sort(lessonPlanSorter).map((lp, index) => {
		// lp.name = `Lesson ${lp.sequence}`;
		// lp.sequence = index + 1;
		lp.name = methodology === MethodologyTypeEnum.REP ? `Playlist ${index + 1}` : `Lesson ${index + 1}`;
		lp.estimatedTime4Minute = second2Minute(lp.estimatedTime as number);
		return lp;
	});
}

@Module({ namespaced: true, name: "LessonPlanListState", store: store, dynamic: true })
export default class LessonPlanListState extends VuexModule {
	versionGroupService: IVersionGroupService = VersionGroupService();
	lessonPlanVersionService: ILessonPlanVersionService = LessonPlanVersionService();
	unitListManager: IUnitListManager = UnitListManagerService();
	lessonPlanListService: ILessonPlanListService = LessonPlanListService();
	versionGroups: VersionGroupModel[] = [];
	lessonPlans: ILessonPlanListModel[] = [];
	allUnits: VCSLevelModel[] = [];
	currentVersionGroups: VersionGroupModel[] = [];
	currentVersionGroup: VersionGroupModel = {} as VersionGroupModel;
	currentMethodology: MethodologyTypeEnum = MethodologyTypeEnum.Classic;
	currentLessonPlanVersions: ILessonPlanVersionModel[] = [];
	currentLessonPlanVersion: ILessonPlanVersionModel = {} as ILessonPlanVersionModel;
	originCurrentLessonPlanVersion: ILessonPlanVersionModel = {} as ILessonPlanVersionModel;
	currentUnits: VCSLevelModel[] = [];
	originCurrentUnits: VCSLevelModel[] = [];
	currentUnit: VCSLevelModel = {} as VCSLevelModel;
	originCurrentUnit: VCSLevelModel = {} as VCSLevelModel;
	currentFrontMatterURL: string = "";
	currentFrontMatterFileName: string = "";
	currentFrontMatterUploadProgress: number = 0;
	isFrontMatterURLBeingDeleted: boolean = false;
	isFrontMatterBeingUploaded: boolean = false;
	loading: boolean = false;
	masterPlanModule = getModule(MasterPlanState);

	@Mutation
	setVersionGroups(versionGroups: VersionGroupModel[]): void {
		this.versionGroups = versionGroups;
	}

	@Mutation
	async setCurrentVersionGroups(versionGroups: VersionGroupModel[]): Promise<void> {
		this.currentVersionGroups = versionGroups;
	}

	@Mutation
	async setCurrentVersionGroup(versionGroup: VersionGroupModel): Promise<void> {
		this.currentVersionGroup = versionGroup;
	}

	@Mutation
	async setCurrentMethodology(methodology: MethodologyTypeEnum): Promise<void> {
		this.currentMethodology = methodology;
	}

	@Mutation
	setCurrentLessonPlanVersions(lessonPlanVersions: ILessonPlanVersionModel[]): void {
		this.currentLessonPlanVersions = lessonPlanVersions;
	}

	@Mutation
	setIsFrontMatterBeingUploaded(res: boolean): void {
		this.isFrontMatterBeingUploaded = res;
	}

	@Mutation
	setIsFrontMatterBeingDeleted(res: boolean): void {
		this.isFrontMatterURLBeingDeleted = res;
	}

	@Mutation
	setCurrentFrontMatterUploadProgress(progress: number): void {
		this.currentFrontMatterUploadProgress = progress;
	}

	@Mutation
	setFrontMatterURL(URL: string): void {
		this.currentFrontMatterURL = URL;
		//set Name of file from URL
		this.currentFrontMatterFileName = URL.replace(/^.*[\\\/]/, "");
	}

	@Mutation
	setCurrentLessonPlanVersion(lessonPlanVersion: ILessonPlanVersionModel): void {
		this.currentLessonPlanVersion = lessonPlanVersion;
		this.currentUnits = filterUnits(
			this.allUnits,
			getLicenseTypeByLessonPlanVersion(
				lessonPlanVersion,
				VersionGroupModule.selectedGsVersion,
				VersionGroupModule.selectedLsVersion
			)
		);
		this.currentUnit = this.currentUnits.length > 0 ? this.currentUnits[0] : ({} as VCSLevelModel);
	}

	@Mutation
	setLessonPlans(lessonPlans: ILessonPlanListModel[]): void {
		this.lessonPlans = lessonPlans;
	}

	@Mutation
	async setAllUnits(units: VCSLevelModel[]): Promise<void> {
		this.allUnits = units;
	}

	@Mutation
	async setCurrentUnits(units: VCSLevelModel[]): Promise<void> {
		this.currentUnits = units;
	}

	// 	@Mutation
	// 	async setCurrentUnitsByVersionGroup(versionGroups: VersionGroupModel[]): Promise<void> {
	// 		this.currentUnits = filterUnits(this.allUnits, versionGroups);
	// 		this.currentUnit = this.currentUnits.length > 0 ? this.currentUnits[0] : ({} as VCSLevelModel);
	// 	}

	@Mutation
	async setCurrentUnitsByLessonPlanVersion(lessonPlanVersion: ILessonPlanVersionModel): Promise<void> {
		this.currentUnits = filterUnits(
			this.allUnits,
			getLicenseTypeByLessonPlanVersion(
				lessonPlanVersion,
				VersionGroupModule.selectedGsVersion,
				VersionGroupModule.selectedLsVersion
			)
		);
		this.currentUnit = this.currentUnits.length > 0 ? this.currentUnits[0] : ({} as VCSLevelModel);
	}

	@Mutation
	async setCurrentUnit(unit: VCSLevelModel): Promise<void> {
		this.currentUnit = unit;
	}

	@Action
	async fetchLessonPlans({
		versionGroupId,
		methodology,
		lessonPlanVersionId,
		unitId
	}: {
		versionGroupId?: string;
		methodology?: string;
		lessonPlanVersionId?: string;
		unitId?: number;
	}): Promise<void> {
		this.setLoading(true);

		// const versionGroups: VersionGroupModel[] = await this.versionGroupService.getVersions(false);
		const versionGroups: VersionGroupModel[] = VersionGroupModule.versionGroups;
		const { selectedGSVersion, selectedLSVersion } =
			VersionGroupModule.selectedGsVersion === "-1" && VersionGroupModule.selectedLsVersion === "-1"
				? await this.versionGroupService.getVcsSettings()
				: {
						selectedGSVersion: VersionGroupModule.selectedGsVersion,
						selectedLSVersion: VersionGroupModule.selectedLsVersion
				  };
		const selectedVersionGroups: VersionGroupModel[] = getSelectedVersionGroups(
			versionGroups,
			selectedGSVersion,
			selectedLSVersion
		);
		// const selectedVersionGroup: VersionGroupModel =
		// 	selectedVersionGroups.length > 0 ? selectedVersionGroups[0] : ({} as VersionGroupModel);
		// const mergedSelectedVersionGroup: VersionGroupModel = versionGroupId
		// 	? versionGroups.find((vg) => vg.id === (versionGroupId as string)) || ({} as VersionGroupModel)
		// 	: selectedVersionGroup;

		const mergedMethodology: number = methodology
			? parseInt(methodology as string, 10)
			: MethodologyTypeEnum.Classic;
		const lessonPlanVersions: ILessonPlanVersionModel[] =
			selectedVersionGroups.length === 0
				? []
				: await this.lessonPlanVersionService.getLessonPlanVersions(
						selectedVersionGroups.map((svg) => svg.id),
						mergedMethodology
				  );
		const sortedLessonPlanVersions: ILessonPlanVersionModel[] = lessonPlanVersions.sort(sortLessonPlanVersions);
		const selectedLessonPlanVersion: ILessonPlanVersionModel =
			sortedLessonPlanVersions.length > 0 ? sortedLessonPlanVersions[0] : ({} as ILessonPlanVersionModel);
		const mergedLessonPlanVersion: ILessonPlanVersionModel = lessonPlanVersionId
			? lessonPlanVersions.find((lpv) => lpv.id === (lessonPlanVersionId as string)) ||
			  ({} as ILessonPlanVersionModel)
			: selectedLessonPlanVersion;

		const allUnits: VCSLevelModel[] = await TitleListService.getLevels([], true);
		const currentUnits: VCSLevelModel[] = filterUnits(
			allUnits,
			getLicenseTypeByLessonPlanVersion(
				mergedLessonPlanVersion,
				VersionGroupModule.selectedGsVersion,
				VersionGroupModule.selectedLsVersion
			)
		);
		const currentUnit: VCSLevelModel =
			currentUnits.length > 0
				? unitId
					? currentUnits.find((u) => u.id === unitId) || ({} as VCSLevelModel)
					: currentUnits[0]
				: ({} as VCSLevelModel);
		let lessonPlans: ILessonPlanListModel[] =
			mergedLessonPlanVersion.id && currentUnit.id
				? await this.lessonPlanListService.getLessonPlanStatistics(
						mergedLessonPlanVersion.id as string,
						currentUnit.id
				  )
				: [];

		if (
			mergedMethodology === MethodologyTypeEnum.Independent &&
			selectedLessonPlanVersion &&
			selectedLessonPlanVersion.id &&
			currentUnit &&
			currentUnit.id
		) {
			var expConvCounts = await this.lessonPlanListService.getExposureConversationCount(
				selectedLessonPlanVersion.id as string,
				currentUnit.id
			);

			lessonPlans = lessonPlans.reduce((acc, lessonPlan) => {
				const model = expConvCounts.find((lp) => lp.lessonPlanId == lessonPlan.id);

				if (model) {
					const { exposureCount, exposureWithConversationCount: conversationCount } = model;
					lessonPlan = { ...lessonPlan, exposureCount, conversationCount };
				} else {
					lessonPlan = { ...lessonPlan, exposureCount: 0, conversationCount: 0 };
				}

				acc.push(lessonPlan);
				return acc;
			}, [] as ILessonPlanListModel[]);
		}

		// this.setVersionGroups(versionGroups);
		// this.setCurrentVersionGroup(mergedSelectedVersionGroup);
		this.setCurrentVersionGroups(selectedVersionGroups);
		this.setCurrentMethodology(mergedMethodology);
		this.setCurrentLessonPlanVersions(sortedLessonPlanVersions);
		this.setCurrentLessonPlanVersion(mergedLessonPlanVersion);
		this.setAllUnits(allUnits);
		this.setCurrentUnits(currentUnits);
		this.setCurrentUnit(currentUnit);
		this.setLessonPlans(formatLessonPlans(lessonPlans, mergedMethodology));
		const archiveData = {
			lessonPlanVersion: mergedLessonPlanVersion,
			unit: currentUnit,
			units: currentUnits
		};
		this.handleOriginLessonAndUnit(archiveData);
		this.setLoading(false);
	}

	@Mutation
	handleOriginLessonAndUnit(data?: {
		lessonPlanVersion: ILessonPlanVersionModel;
		unit: VCSLevelModel;
		units: VCSLevelModel[];
	}) {
		if (data) {
			// archived origin data
			const { lessonPlanVersion, unit, units } = data;
			this.originCurrentLessonPlanVersion = lessonPlanVersion;
			this.originCurrentUnit = unit;
			this.originCurrentUnits = units;
		} else {
			// reset data to origin
			this.currentLessonPlanVersion = this.originCurrentLessonPlanVersion;
			this.currentUnit = this.originCurrentUnit;
			this.currentUnits = this.originCurrentUnits;
		}
	}

	@Action
	async resetLessonAndUnitToOrigin() {
		this.handleOriginLessonAndUnit();
	}

	@Action
	async fetchLessonPlanVersions({
		versionGroup,
		methodology
	}: {
		versionGroup?: VersionGroupModel;
		methodology?: MethodologyTypeEnum;
	}): Promise<ILessonPlanVersionModel> {
		this.setLoading(true);

		// const mergedVersionGroup: VersionGroupModel = versionGroup ? versionGroup : this.currentVersionGroup;
		const versionGroups: VersionGroupModel[] = VersionGroupModule.versionGroups;
		const selectedVersionGroups: VersionGroupModel[] = getSelectedVersionGroups(
			versionGroups,
			EmptyGuid,
			EmptyGuid
		);
		const mergedMethodology: MethodologyTypeEnum =
			methodology === undefined ? this.currentMethodology : methodology;
		const lessonPlanVersions: ILessonPlanVersionModel[] =
			selectedVersionGroups.length === 0
				? []
				: await this.lessonPlanVersionService.getLessonPlanVersions(
						selectedVersionGroups.map((svg) => svg.id),
						mergedMethodology
				  );
		const sortedLessonPlanVersions: ILessonPlanVersionModel[] = lessonPlanVersions.sort(sortLessonPlanVersions);
		const selectedLessonPlanVersion: ILessonPlanVersionModel =
			sortedLessonPlanVersions.length > 0 ? sortedLessonPlanVersions[0] : ({} as ILessonPlanVersionModel);

		this.setCurrentVersionGroups(selectedVersionGroups);
		// this.setCurrentUnitsByVersionGroup(selectedVersionGroups);
		this.setCurrentUnitsByLessonPlanVersion(selectedLessonPlanVersion);

		// 		if (versionGroup) {
		// 			this.setCurrentVersionGroup(versionGroup);
		// 		}

		if (methodology !== undefined) {
			this.setCurrentMethodology(methodology);
		}

		const promise = this.refreshLessonPlans({
			lessonPlanVersion: selectedLessonPlanVersion,
			methodology: mergedMethodology
		});
		this.setCurrentLessonPlanVersions(sortedLessonPlanVersions);
		this.setCurrentLessonPlanVersion(selectedLessonPlanVersion);

		if (promise) {
			promise
				.then(() => {
					this.setLoading(false);
				})
				.catch(() => {
					this.setLoading(false);
				});
		} else {
			this.setLoading(false);
		}
		return selectedLessonPlanVersion;
	}

	@Action
	async refreshLessonPlans({
		lessonPlanVersion,
		methodology,
		unit
	}: {
		lessonPlanVersion?: ILessonPlanVersionModel;
		methodology?: MethodologyTypeEnum;
		unit?: VCSLevelModel;
	}): Promise<void> {
		const mergedLessonPlanVersion: ILessonPlanVersionModel = lessonPlanVersion
			? lessonPlanVersion
			: this.currentLessonPlanVersion;
		const mergedMethodology: MethodologyTypeEnum =
			methodology === undefined ? this.currentMethodology : methodology;
		const mergedUnit: VCSLevelModel = unit ? unit : this.currentUnit;
		if (mergedLessonPlanVersion && mergedLessonPlanVersion.id && mergedUnit && mergedUnit.id) {
			let lessonPlans: ILessonPlanListModel[] = await this.lessonPlanListService.getLessonPlanStatistics(
				mergedLessonPlanVersion.id as string,
				mergedUnit.id
			);

			if (mergedMethodology === MethodologyTypeEnum.Independent) {
				var expConvCounts = await this.lessonPlanListService.getExposureConversationCount(
					mergedLessonPlanVersion.id,
					mergedUnit.id
				);

				lessonPlans = lessonPlans.reduce((acc, lessonPlan) => {
					const model = expConvCounts.find((lp) => lp.lessonPlanId == lessonPlan.id);

					if (model) {
						const { exposureCount, exposureWithConversationCount: conversationCount } = model;
						lessonPlan = { ...lessonPlan, exposureCount, conversationCount };
					} else {
						lessonPlan = { ...lessonPlan, exposureCount: 0, conversationCount: 0 };
					}

					acc.push(lessonPlan);
					return acc;
				}, [] as ILessonPlanListModel[]);
			}

			this.setLessonPlans(formatLessonPlans(lessonPlans, mergedMethodology));
		} else {
			this.setLessonPlans([]);
		}
	}

	@Action
	async saveLessonPlanData({
		lessonPlan,
		action
	}: {
		lessonPlan?: ILessonPlanListModel;
		action: LessonPlanActionStatus;
	}): Promise<void> {
		this.setLoading(true);
		const maxSequence: number = Math.max(...this.lessonPlans.map((lp) => lp.sequence as number));
		switch (action) {
			case LessonPlanActionStatus.Add:
				await this.lessonPlanListService.createLessonPlan({
					...(lessonPlan ? lessonPlan : {}),
					lessonPlanVersionId: this.currentLessonPlanVersion.id,
					unitId: this.currentUnit.id,
					sequence: maxSequence === -Infinity ? 1 : maxSequence + 1
				});
				break;
			default:
				break;
		}
		this.refreshLessonPlans({});
		this.setLoading(false);
	}

	@Action
	async saveLessonPlansData({
		action,
		lessonPlanCount
	}: {
		action: LessonPlanActionStatus;
		lessonPlanCount: number;
	}): Promise<void> {
		this.setLoading(true);
		const lessonPlansToCreate: ILessonPlanListModel[] = [];
		for (let index = 0; index < lessonPlanCount; index++) {
			lessonPlansToCreate?.push({
				lessonPlanVersionId: this.currentLessonPlanVersion.id,
				unitId: this.currentUnit.id
			});
		}
		switch (action) {
			case LessonPlanActionStatus.Add:
				await this.lessonPlanListService.createLessonPlans(lessonPlansToCreate);
				break;
			default:
				break;
		}
		this.refreshLessonPlans({});
		this.setLoading(false);
	}

	@Action
	async swapLessonPlans({
		preLessonPlanId,
		nextLessonPlanId
	}: {
		preLessonPlanId: string;
		nextLessonPlanId: string;
	}) {
		this.setLoading(true);
		await this.lessonPlanListService.swapLessonPlans(preLessonPlanId, nextLessonPlanId);
		this.refreshLessonPlans({});
		this.setLoading(false);
	}

	@Action
	async deleteLessonPlan(lessonPlanId: string): Promise<void> {
		this.setLoading(true);
		await this.lessonPlanListService.deleteLessonPlan(lessonPlanId);
		this.refreshLessonPlans({});
		this.setLoading(false);
	}

	@Action
	async downloadLessonPlans({
		template,
		lessonPlanVersionId,
		unitId,
		sequenceId
	}: {
		template: ILessonPlanTemplateModel;
		lessonPlanVersionId: string;
		unitId: number;
		sequenceId?: number | undefined;
	}) {
		this.setLoading(true);
		const id =
			(this.masterPlanModule.currentUnit &&
				this.masterPlanModule.currentUnit.curriculumType == CurriculumType.GrapeSEED) ||
			this.currentUnit.curriculumType == CurriculumType.GrapeSEED
				? VersionGroupModule.selectedGsVersion
				: VersionGroupModule.selectedLsVersion;

		const versionGroup = VersionGroupModule.versionGroups.find((v) => v.id == id)!;
		const methodology = MethodologyTypeItemsWithAll.get(this.masterPlanModule.currentMethodology);
		const unitName = this.masterPlanModule.currentUnit.displayName || this.currentUnit.displayName;
		const fileName = sequenceId
			? `LessonPlan-${versionGroup.name}-${methodology}-${unitName}-Lesson ${sequenceId}(${template.name})`
			: `LessonPlan-${versionGroup.name}-${methodology}-${unitName}(${template.name})`;
		await this.lessonPlanListService
			.exportLessonPlan2PDF(template.id as string, lessonPlanVersionId, unitId, sequenceId, fileName)
			.then((data) => {
				BlobHelper.saveFile(data);
				this.setLoading(false);
			})
			.catch((reason) => {
				//console.log(reason);
				this.setLoading(false);
			});
	}

	@Mutation
	setLoading(loading: boolean): void {
		this.loading = loading;
	}
}
